<template>
  <el-drawer
    :visible.sync="showDrawer"
    :before-close="handleClose"
    :size="drawerWidth"
    :show-close="false"
  >
    <div class="flex items-center justify-between border-b pb-6 px-6">
      <h2 class="text-xl">{{ drawerTitle }}</h2>
      <span class="cursor-pointer" @click="handleClose">
        <close-icon size="h-6 w-6" />
      </span>
    </div>

    <el-form
      label-position="top"
      :model="form"
      ref="form"
      :rules="rules"
      @submit.native.prevent="validateForm('form')"
      @keyup.enter.native="validateForm('form')"
    >
      <div class="p-6">
        <el-form-item label="Email" prop="email">
          <el-input
            type="email"
            v-model="form.email"
            placeholder="name@example.com"
          >
          </el-input>
        </el-form-item>

        <el-form-item label="Role" prop="userKind">
          <el-select
            class="w-full"
            v-model="form.userKind"
            placeholder="Select"
          >
            <el-option
              v-for="item in roles"
              :key="item.value"
              :value="item.value"
              :label="item.subTitle || item.title"
              class="option-container"
            >
              <div class="flex flex-col gap-2 text-sm">
                <h4 class="font-semibold text-black-10">
                  {{ item.subTitle || item.title }}
                </h4>
                <p class="text-gray-muted">
                  {{ item.subBody || item.body }}
                </p>
              </div>
            </el-option>
          </el-select>
        </el-form-item>

        <div v-if="isUserApprover" class="mt-6">
          <span class="text-gray-muted font-normal text-sm">
            Set Transaction limit for this user
          </span>

          <el-form-item label="Amount" prop="amount">
            <el-input v-model="form.amount">
              <i
                slot="prefix"
                class="font-normal text-black-10 h-12 inline-flex items-center not-italic pl-3"
                >$</i
              >
            </el-input>
          </el-form-item>

          <p class="text-gray-muted -mt-1 font-normal text-sm">
            This user will only be able to approve transactions equal to or less
            than
            <span class="text-black-10 font-medium"> $ {{ form.amount }}</span>
          </p>

          <div
            class="flex gap-2 my-6 bg-yellow-7 rounded-lg py-2 px-3 border border-yellow-8"
          >
            <spn>
              <info-icon color="#BA8015" />
            </spn>
            <span class="font-light text-sm text-yellow-8">
              Transactions in any currency will be converted to ensure
              equivalence with this set limit
            </span>
          </div>
        </div>

        <button
          type="submit"
          class="w-full font-normal py-4 bg-primary text-white text-sm rounded-lg mt-8"
        >
          {{ buttonTitle }}
        </button>
      </div>
    </el-form>
  </el-drawer>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import { ROLES } from '@/utils/constants.js'
import { ParseAmount } from '@/utils/helpers.js'

export default {
  name: 'AddNewUser',
  components: {
    CloseIcon: () => import('@/assets/v2/icons/CloseIcon.vue'),
    InfoIcon: () => import('@/assets/v2/icons/InfoTwoIcon.vue')
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    details: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      breakpoints,
      form: {
        userKind: '',
        email: '',
        amount: ''
      },
      rules: {
        email: [
          {
            required: true,
            type: 'email',
            message: 'Kindly enter a valid email.',
            trigger: ['blur', 'change']
          }
        ],
        userKind: [
          {
            required: true,
            message: 'Kindly select a role',
            trigger: ['blur', 'change']
          }
        ],
        amount: [
          {
            required: true,
            message: 'Kindly set an transaction limit',
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  computed: {
    drawerWidth () {
      return this.breakpoints.lgAndUp
        ? '30%'
        : this.breakpoints.mdAndUp
        ? '55%'
        : '90%'
    },
    isUserApprover () {
      return this.form.userKind === 3 || this.form.userKind === 5
    },
    roles () {
      return ROLES.filter((item) => item.value > 1)
    },
    drawerTitle () {
      switch (this.type) {
        case 'inviteUser':
          return 'Invite A User'
        case 'reassignUser':
          return 'Edit Access'
        default:
          return ''
      }
    },
    buttonTitle () {
      switch (this.type) {
        case 'inviteUser':
          return 'Send Invite'
        case 'reassignUser':
          return 'Save'
        default:
          return ''
      }
    }
  },
  methods: {
    handleClose () {
      this.$emit('closeDrawer')
    },
    prefillForm () {
      this.form.userKind = this.details.kind
      this.form.email = this.details.email
      this.form.amount = this.details.transactionLimit || ''
    },
    validateForm (form) {
      this.$refs[form].validate((valid) => {
        if (valid) {
          this.$emit('openVerify', this.form)
          this.handleClose()
        } else {
          this.$notify.error({
            title: 'Invalid details',
            message: 'Please fill in required details',
            position: 'top-right'
          })
        }
      })
    }
  },
  watch: {
    'form.amount': function (newValue) {
      const result = ParseAmount(newValue)
      this.$nextTick(() => {
        this.form.amount = result
      })
    }
  },
  mounted () {
    if (this.type === 'reassignUser') {
      this.prefillForm()
    }
  }
}
</script>

<style scoped>
.white-space {
  white-space: normal;
  display: flex;
  gap: 3px;
  align-items: center;
}
.option-container {
  height: auto;
  width: 384px;
  display: flex;
  flex-direction: column;
  padding: 10px 20px;
  margin: 0;
  border-bottom: 1px solid #e1e3f2;
  white-space: normal;
}
.option-container:last-child {
  border-bottom: none;
}
>>> .el-drawer__header {
  padding: 0;
}
>>> .el-drawer {
  background: var(--background-canvas) !important;
}
>>> .el-select-dropdown__item,
.selected p {
  color: var(--color-primary);
  font-weight: normal;
}
>>> .el-select-dropdown__item,
.selected h4 {
  color: var(--color-primary);
}
>>> .el-input__inner {
  border-radius: 8px;
  border-color: #c7c8d2;
  height: 48px;
  color: #171717;
}
>>> .el-select .el-input__inner:focus {
  border-radius: 8px;
  border-color: #c7c8d2;
}
>>> .el-form--label-top .el-form-item__label {
  padding: 0;
}
>>> .el-form-item {
  margin-bottom: 20px;
}
>>> .el-checkbox__input {
  transform: scale(1.3);
}
>>> .el-checkbox__inner:hover,
>>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: var(--color-primary);
}
>>> .el-checkbox__input.is-checked + .el-checkbox__label,
>>> .el-checkbox__label {
  font-weight: normal;
  color: #5d5e68d1;
}
</style>
